import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import type { FormField, TableColumnDraft } from "../../types";
import type { Variable } from "../../../types";
import { ROBUST_DATE_FORMATS, type BaseField } from "../types";
import {
  deformatDefaultValue,
  type DefaultValueObject,
} from "./deformat-value";
import {
  mapConditionTypeBackName,
  mapFieldTypeBack,
} from "./deformat-mappings";

dayjs.extend(customParseFormat);

const parseDateRobust = (value: string | undefined | null) => {
  if (!value) return null;
  const parsed = dayjs(value, ROBUST_DATE_FORMATS, true);
  return parsed.isValid() ? parsed : null;
};

/**
 * Stringifies primitive default values only. Objects (variable / interoperability
 * references) have no textual representation here, so they resolve to an empty
 * string instead of "[object Object]".
 */
const stringifyPrimitiveDefault = (defaultValue: unknown): string => {
  if (defaultValue == null || typeof defaultValue === "object") return "";
  if (typeof defaultValue === "string") return defaultValue;
  if (typeof defaultValue === "number" || typeof defaultValue === "boolean") {
    return String(defaultValue);
  }
  return "";
};

const formatSwitchDefault = (defaultValue: unknown): string => {
  if (typeof defaultValue === "boolean") {
    return defaultValue ? "true" : "false";
  }
  return "";
};

const formatYearDefault = (defaultValue: unknown): string =>
  stringifyPrimitiveDefault(defaultValue);

const formatDateDefault = (defaultValue: unknown): string => {
  const dateStr =
    typeof defaultValue === "string" || typeof defaultValue === "number"
      ? String(defaultValue)
      : null;
  if (!dateStr) return stringifyPrimitiveDefault(defaultValue);
  const parsed = dayjs(dateStr, ROBUST_DATE_FORMATS, true);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : dateStr;
};

const defaultValueFormatter = (fieldType: string, field: BaseField): string => {
  const defaultValue = field.default_value;

  if (defaultValue == null) {
    return "";
  }

  if (fieldType === "switch") return formatSwitchDefault(defaultValue);
  if (fieldType === "year") return formatYearDefault(defaultValue);
  if (fieldType.includes("date")) return formatDateDefault(defaultValue);

  return stringifyPrimitiveDefault(defaultValue);
};

function normalizeDate(value: string | undefined | null) {
  if (!value) return undefined;
  const parsed = dayjs(value, ROBUST_DATE_FORMATS, true);
  if (!parsed.isValid()) return undefined;
  return parsed.format("DD/MM/YYYY");
}

function resolveConfigDefaultValue(baseField: BaseField): string {
  const isDefaultValueObj =
    baseField.default_value && typeof baseField.default_value === "object";
  const dv = isDefaultValueObj
    ? (baseField.default_value as DefaultValueObject & {
        files?: Array<{ name?: string }>;
      })
    : undefined;

  if (
    dv?.type === "interoperability" ||
    dv?.type?.toLowerCase() === "variable"
  ) {
    return "";
  }
  if (baseField.type === "file" && dv?.type?.toUpperCase() === "STATIC") {
    return dv.files?.[0]?.name ?? "";
  }
  return defaultValueFormatter(baseField.type ?? "", baseField);
}

function resolveConfigDefaultTimeValue(baseField: BaseField): string {
  if (
    !(baseField.type ?? "").includes("date") ||
    !baseField.config.includeTime ||
    !baseField.default_value
  ) {
    return "";
  }
  const dateStr =
    typeof baseField.default_value === "string" ||
    typeof baseField.default_value === "number"
      ? String(baseField.default_value)
      : null;
  const parsed = parseDateRobust(dateStr);
  if (!parsed) return "";
  return parsed.format(
    baseField.config.time_format === "12h" ? "hh:mm A" : "HH:mm",
  );
}

function resolveDatePart(
  value: string | undefined | null,
  timeFormat: "12h" | "24h" | undefined,
): { date: string; time: string } {
  const parsed = parseDateRobust(value);
  if (!parsed) return { date: "", time: "" };
  return {
    date: parsed.format("DD/MM/YYYY"),
    time: parsed.format(timeFormat === "12h" ? "hh:mm A" : "HH:mm"),
  };
}

function resolveDefaultValueType(baseField: BaseField): "var" | "cam" | "inp" {
  const linkedVar = baseField.config?.linked_variable;
  const isLinkedVarObj = linkedVar && typeof linkedVar === "object";
  const isDefaultValueObj =
    baseField.default_value && typeof baseField.default_value === "object";

  const dv = baseField.default_value as DefaultValueObject | undefined;
  if (
    (isLinkedVarObj && linkedVar.type?.toLowerCase() === "variable") ||
    (isDefaultValueObj && dv?.type?.toLowerCase() === "variable")
  ) {
    return "var";
  }

  if (
    (isLinkedVarObj && linkedVar.type?.toLowerCase() === "interoperability") ||
    (isDefaultValueObj && dv?.type?.toLowerCase() === "interoperability")
  ) {
    return "cam";
  }

  return "inp";
}

function applyDateFieldConfig(field: FormField, baseField: BaseField): void {
  if (baseField.type === "year") {
    field.datePrecision = "year";
    const cfg = baseField.config;
    field.yearRangeType = cfg.year_range_type ?? "fixed";
    if (cfg.year_range_type === "relative") {
      field.useRelativeDate = true;
      field.relativeMinDate = {
        value: Math.abs(cfg.year_offset_min ?? 0).toString(),
        unit: "years",
        adjustment: (cfg.year_offset_min ?? 0) > 0 ? "after" : "before",
      };
      field.relativeMaxDate = {
        value: Math.abs(cfg.year_offset_max ?? 0).toString(),
        unit: "years",
        adjustment: (cfg.year_offset_max ?? 0) >= 0 ? "after" : "before",
      };
    } else {
      field.useRelativeDate = false;
      field.minYear = cfg.min_year ?? null;
      field.maxYear = cfg.max_year ?? null;
    }
    return;
  }

  const rawMin = baseField.config.min_datetime || baseField.config.min_date;
  const rawMax = baseField.config.max_datetime || baseField.config.max_date;
  const isNow = (val: string | null | undefined) =>
    typeof val === "string" &&
    val.trim().toLowerCase().replace(/[()]/g, "") === "now";

  field.minDate = isNow(rawMin) ? "now" : normalizeDate(rawMin);
  field.maxDate = isNow(rawMax) ? "now" : normalizeDate(rawMax);
  field.includeTime = baseField.config.includeTime;
  field.format24Hour = baseField.config.time_format === "24h";
  field.useRelativeDate = baseField.config.is_relative_min;
  field.relativeMinDate = {
    value: Math.abs(baseField.config.relative_min_value ?? 0).toString(),
    adjustment:
      (baseField.config.relative_min_value ?? 0) > -1 ? "after" : "before",
    unit: baseField.config.relative_min_unit ?? "",
  };
  field.relativeMaxDate = {
    value: Math.abs(baseField.config.relative_max_value ?? 0).toString(),
    adjustment:
      (baseField.config.relative_max_value ?? 0) > -1 ? "after" : "before",
    unit: baseField.config.relative_max_unit ?? "",
  };
}

const decimalsDeformat = (precision: number | null, allow?: boolean) => {
  if (precision === null) {
    return allow === false ? 0 : null;
  }
  return precision;
};

const arrayToString = (arr: unknown[]) => arr.join(" / ");

interface FileDefaultValue {
  type?: string;
  file_uuids?: string[];
  files?: Array<{
    id: string;
    name: string;
    url?: string;
    size?: number;
    mime_type?: string;
  }>;
}

function resolveFileFieldDefaultFiles(dv: FileDefaultValue) {
  if (Array.isArray(dv.files) && dv.files.length > 0) {
    return dv.files.map((f) => ({
      id: f.id,
      uuid: f.id,
      name: f.name,
      url: f.url,
      size: f.size,
      mime_type: f.mime_type,
    }));
  }
  if (Array.isArray(dv.file_uuids) && dv.file_uuids.length > 0) {
    return dv.file_uuids.map((uuid) => ({
      id: uuid,
      uuid,
      name: "Archivo adjunto",
    }));
  }
  return [];
}

function applySelectFieldConfig(field: FormField, baseField: BaseField): void {
  field.options = baseField.config.options
    ? baseField.config.options.map((opt) => opt.label)
    : [];
  field.is_var_by_registry = baseField.config.is_var_by_registry;
  field.multiple = baseField.config.multiple;
  field.valueSource = baseField.config.options_type;
  const selectDefault = baseField.default_value as
    | { options?: { value: string; label: string }[] }
    | string
    | null
    | undefined;
  field.selectDefaultOptions =
    selectDefault &&
    typeof selectDefault === "object" &&
    Array.isArray(selectDefault.options)
      ? selectDefault.options
      : [];
  field.configDefaultValue = "";
  field.variableRef = {
    type: baseField.config.data_center_value_type ?? undefined,
    groupId: baseField.config.data_center_node_id ?? "",
    groupIcon: {
      className: "text-info-quiet",
      name: "table",
      size: "xs",
      type: "far",
    },
    groupLabels: [
      baseField.config.data_center_node ?? "",
      baseField.config.data_center_table ?? "",
    ],
    itemIcon: {
      name: "columns",
      type: "far",
    },
    itemLabel: baseField.config.data_center_column ?? "",
    itemValue: baseField.config.data_center_column ?? "",
  };
}

function applyFileFieldConfig(field: FormField, baseField: BaseField): void {
  field.fileTypes = baseField.config.allowed_mimes || [];
  field.multipleFiles = baseField.config.multiple || false;
  field.allowAnyFile = baseField.config.allowed_mimes?.includes("*") || false;
  field.isNitHomologado = baseField?.config?.isNitHomologado;
  field.maxFileSize =
    baseField.config.max_file_size_mb || baseField.config.max_total_size_mb;
  field.fileSizeKey = !baseField.config.max_file_size_mb;
  if (baseField.config.external_validation?.enabled) {
    field.externalValidation = {
      enabled: true,
      workflow: baseField.config.external_validation.workflow ?? "",
    };
  }

  const dv = baseField.default_value as FileDefaultValue | null | undefined;
  if (dv && typeof dv === "object" && dv.type?.toUpperCase() === "STATIC") {
    field.defaultValueType = "inp";
    const defaultFiles = resolveFileFieldDefaultFiles(dv);
    if (defaultFiles.length > 0) {
      field.defaultFiles = defaultFiles;
      field.defaultFile = defaultFiles[0];
      field.configDefaultValue = defaultFiles[0].name;
    }
  }
}

function applyInteroperabilityFieldConfig(
  field: FormField,
  baseField: BaseField,
): void {
  field.service = baseField.config.service;
  field.action = baseField.config.action;
  field.input_field = baseField.config.input_field?.map((input) => {
    if (input.placeholder && /^\d{4}-\d{2}-\d{2}$/.test(input.placeholder)) {
      const [year, month, day] = input.placeholder.split("-");
      return { ...input, placeholder: `${day}/${month}/${year}` };
    }
    return input;
  });
  field.output_values = baseField.config.output_values;
}

function applyFieldTypeConfig(field: FormField, baseField: BaseField): void {
  switch (field.type) {
    case "text":
      field.maxLength = baseField.config.max_length || undefined;
      field.mask = baseField.config.mask || undefined;
      break;
    case "textarea":
      field.maxLength = baseField.config.max_length || undefined;
      break;
    case "email":
      field.allowedDomains = baseField.config.allowed_domains || undefined;
      field.disallowedDomains = baseField.config.blocked_domains || undefined;
      field.maxLength = baseField.config.max_length || undefined;
      break;
    case "number":
      field.maxLength = baseField.config.max?.toString().length || undefined;
      field.maxValue = baseField.config.max;
      field.minValue = baseField.config.min;
      field.precision = decimalsDeformat(
        baseField.config.precision ?? null,
        baseField.config.allow_decimals,
      );
      field.numberFormat = baseField.config.number_format ?? "normal";
      break;
    case "select":
      applySelectFieldConfig(field, baseField);
      break;
    case "datetime":
    case "date":
    case "date_range":
    case "datetime_range":
      applyDateFieldConfig(field, baseField);
      break;
    case "file":
      applyFileFieldConfig(field, baseField);
      break;
    case "phone":
      field.allowCountry = baseField.config.allow_extensions || false;
      break;
    case "identification":
      field.allowUserChooseDocType =
        baseField.config.allow_select_document_type || false;
      field.documentType = baseField.config.document_type;
      break;
    case "interoperability":
      applyInteroperabilityFieldConfig(field, baseField);
      break;
    case "switch":
      field.label = baseField.config.label;
      field.switchActiveLabel = baseField.config.active_label;
      field.switchInactiveLabel = baseField.config.inactive_label;
      break;
    case "expedient":
      field.expedientBaseEntityId =
        baseField.config.expedient_base_entity_id || undefined;
      field.additionalColumns = baseField.config.additional_columns || [];
      field.confirm_reselection = baseField.config.confirm_reselection ?? false;
      field.attachToFormalityRequest =
        baseField.config.attach_to_formality_request ?? false;
      break;
    case "filtered-list":
    case "filtered_select":
      field.levels = baseField.config.levels || [];
      break;
    case "table":
      // El limite de filas viaja en `max_items`, la clave que los campos
      // multivalor usan para su tope; aqui se devuelve a su propia propiedad.
      field.tableMaxRows = baseField.max_items ?? null;
      field.description = baseField.config.description ?? "";
      field.tableColumns = deformatTableColumns(baseField);
      break;
  }
}

/**
 * Reconstruye las columnas del campo tabla.
 *
 * Reutiliza `applyFieldTypeConfig` sobre un `BaseField` sintetico por columna:
 * el mismo switch sirve en las dos direcciones, asi que no hace falta escribir
 * un deserializador por tipo de columna.
 */
function deformatTableColumns(baseField: BaseField): TableColumnDraft[] {
  return (baseField.config.columns ?? []).map((column, index) => {
    // Contraparte de `mapColumnType`: el contrato usa `datetime`, el Builder
    // llama `date` a ese tipo de campo.
    const type = (
      column.type === "datetime" ? "date" : mapFieldTypeBack(column.type)
    ) as TableColumnDraft["type"];
    const typeConfig = { type } as FormField;

    applyFieldTypeConfig(typeConfig, {
      ...baseField,
      type: column.type,
      config: column.settings ?? {},
    });

    return {
      id: column.key || `column-${index}`,
      name: column.name,
      key: column.key,
      type,
      required: column.required,
      placeholder: column.placeholder ?? "",
      typeConfig,
    };
  });
}

function buildFieldConditions(
  baseField: BaseField,
  fieldIndex: number,
  nameToLabel: Record<string, string>,
) {
  if (!baseField.conditions || baseField.conditions.length === 0) return null;

  const conditionsOperator = (baseField.conditions[0]?.operator || "OR") as
    "OR" | "AND";
  const conditions = baseField.conditions.map((c, condIndex) => {
    // A field_reference not found among this form's own fields is a
    // reference to an external variable (e.g. a custom field of an entity
    // linked via an "Expediente relacionado" field) - its field_reference
    // is the variable's output_name, not a sibling field's name.
    const isExternalVariable =
      !!c.field_reference && !(c.field_reference in nameToLabel);
    const refLabel =
      (c.field_reference && nameToLabel[c.field_reference]) ||
      c.field_reference;
    const fieldRef = c.field_reference || "";
    return {
      id: `cond-${fieldIndex + 1}-${condIndex + 1}`,
      data: {
        selectedValue: {
          id: fieldRef,
          name: refLabel,
          type: c.field_reference_type,
          ...(isExternalVariable ? { output_name: fieldRef } : {}),
        },
        selectedOperation: {
          name: c.condition_type || "",
          label: mapConditionTypeBackName(c.condition_type || ""),
          needsComparativeValue: !!c.value,
          comparativeValueType: c.field_reference_type,
        },
        compareValueType: typeof c.value,
        compareInput: Array.isArray(c.value)
          ? arrayToString(c.value)
          : String(c.value ?? ""),
        compareDate:
          c.value instanceof Date ? c.value : (c.value as string) || "",
        selectedVar: {
          id: fieldRef,
          name: fieldRef,
          type: "text",
        },
      },
    };
  });

  return { isConditional: true as const, conditionsOperator, conditions };
}

export function buildDeformField(
  baseField: BaseField,
  fieldIndex: number,
  nameToLabel: Record<string, string>,
): FormField {
  const dv = baseField.default_value as DefaultValueObject | undefined;
  const startParts = resolveDatePart(
    dv?.start ?? undefined,
    baseField.config.time_format,
  );
  const endParts = resolveDatePart(
    dv?.end ?? undefined,
    baseField.config.time_format,
  );

  const field: FormField = {
    id: baseField.id || null,
    name: baseField.label || "",
    technicalKey: baseField.name || `field_${fieldIndex + 1}`,
    type: mapFieldTypeBack(baseField.type),
    placeholder: baseField.placeholder || undefined,
    required: baseField.required || false,
    configDefaultValue: resolveConfigDefaultValue(baseField),
    configDefaultTimeValue: resolveConfigDefaultTimeValue(baseField),
    configDefaultStartDate: startParts.date,
    configDefaultStartTime: startParts.time,
    configDefaultEndDate: endParts.date,
    configDefaultEndTime: endParts.time,
    options: [],
    isConditional: baseField.is_conditional,
    isReadOnly: baseField.is_read_only,
    minValue: baseField.minValue,
    maxValue: baseField.maxValue,
    isPreventable: baseField.is_preventable,
    defaultValueType: resolveDefaultValueType(baseField),
    defaultValueVariable: deformatDefaultValue(baseField) as Variable,
    // Contraparte del formatter: aunque el backend haya guardado `is_array` en
    // true para una tabla, al recargar no debe convertirse en multivalor. Asi
    // el siguiente guardado corrige el dato en vez de repetir el error.
    allowMultipleValues:
      baseField.type === "table" ? false : !!baseField.is_array,
    allowedQuantityOfValues: baseField.max_items ?? null,
  };

  applyFieldTypeConfig(field, baseField);

  const conditionData = buildFieldConditions(
    baseField,
    fieldIndex,
    nameToLabel,
  );
  if (conditionData) {
    field.isConditional = conditionData.isConditional;
    field.conditionsOperator = conditionData.conditionsOperator;
    field.conditions = conditionData.conditions;
  }

  return field;
}
